import { logGreyErrorFromAccounts, logUsageFromAccounts } from 'owa-account-analytics';
import type { CustomDataMap, TraceErrorObject } from 'owa-analytics-types';
import { getClientVersion } from 'owa-config/lib/bootstrapOptions';
import { fetch } from 'owa-fetch';
import { getGuid } from 'owa-guid';
import type { CloudEnvironment } from '../types/CloudEnvironment';
import type { CloudEnvironmentDiscoverer } from '../types/CloudEnvironmentDiscoverer';
import { StepsCustomData } from 'owa-analytics-record-steps';
import { createExponentialBackoffFunction, createRetriableFunction } from 'owa-retriable-function';

const OFFICE_CONFIG_SERVICE_URL = 'https://officeclient.microsoft.com/config16processed';
const FEDERATION_PROVIDER_QUERY_PARAMS = '?services=GetFederationProvider&flights=Client.HrdV21';
const OUTLOOK_WEB_URL_QUERY_PARAM = '?services=OutlookWebUrl';

const commonHeaders = {
    'X-Office-Application': '5',
    'X-Office-Platform': 'Web',
    'Enlightened-Hrd-Client': '1',
    'Content-Type': 'application/json',
};

type HrdResponse = {
    environment: string;
    configProviderName: string | undefined;
    endpoint: EndpointResponse[];
    tenantId: string;
    authority_host: string;
    graph: string;
};

type EndpointResponse = {
    type: string;
    authentication_endpoint: string;
    token_endpoint: string;
};

class InstrumentationContext {
    steps: StepsCustomData;
    startTimeOrigin: number;

    constructor() {
        this.startTimeOrigin = performance.now();
        this.steps = new StepsCustomData();
    }

    addStep(stepName: string, data?: CustomDataMap): void {
        this.steps.addStep(stepName, {
            ...data,
            elapsedMs: performance.now() - this.startTimeOrigin,
        });
    }
}

export class OfficeHrdCloudEnvironmentDiscoverer implements CloudEnvironmentDiscoverer {
    private cachedHrdUrl: string | undefined = undefined;

    async discoverConfigForDomain(domain: string): Promise<CloudEnvironment> {
        const instrumentation = new InstrumentationContext();

        const correlationId = getGuid();
        instrumentation.addStep('Begin', { correlationId, isOnline: self.navigator.onLine });

        try {
            instrumentation.addStep('GetHrdUrl');
            const hrdUrl = await this.getHrdUrl(instrumentation, correlationId);

            const getFederationProviderUrl = addDomainQueryParamToHrdUrl(hrdUrl, domain);

            instrumentation.addStep('FetchFromHrdUrl');
            const urlResponse = await fetchWithRetry(getFederationProviderUrl, {
                method: 'GET',
                headers: new Headers({ ...commonHeaders, 'X-CorrelationId': correlationId }),
            });

            instrumentation.addStep('ReadHrdResponse');
            const response = (await urlResponse.json()) as HrdResponse;

            instrumentation.addStep('BuildCloudEnv', {
                configProviderName: response.configProviderName,
                environment: response.environment,
                tenantId: response.tenantId,
            });
            const cloudEnv = await buildCloudEnvironmentFromHrdResponse(
                instrumentation,
                response,
                correlationId
            );

            instrumentation.addStep('End', {
                outcome: 'Success',
                cloudEnvKey: cloudEnv.environmentKey,
            });
            return cloudEnv;
        } catch (error) {
            /* eslint-disable owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logGreyErrorFromAccounts must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            // This event will catch any issue in the function stream
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
            // @ts-expect-error
            logGreyErrorFromAccounts('OfficeHrdCloudEnvDiscovery_Error', undefined, error);
            /* eslint-enable owa-custom-rules/telemetry-mailboxinfo-as-second-param */

            instrumentation.addStep('End', {
                outcome: 'Error',
                // Errors resulting from enabling useUnknownInCatchVariables
                // TS18046: 'error' is of type 'unknown'.
                // @ts-expect-error
                error: error.message,
                isOnline: self.navigator.onLine,
            });
            throw error;
        } finally {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logUsageFromAccounts must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logUsageFromAccounts(
                'OfficeHrdCloudEnvDiscovery',
                undefined,
                instrumentation.steps.getCustomDataMapForSteps()
            );
        }
    }

    async getHrdUrl(
        instrumentation: InstrumentationContext,
        correlationId: string
    ): Promise<string> {
        if (this.cachedHrdUrl) {
            instrumentation.addStep('GetHrdUrl_FromCache');
            return this.cachedHrdUrl;
        }

        instrumentation.addStep('GetHrdUrl_FromService');
        const hrdUrl = await fetchHrdUrl(correlationId);
        this.cachedHrdUrl = hrdUrl;

        return hrdUrl;
    }
}

async function buildCloudEnvironmentFromHrdResponse(
    instrumentation: InstrumentationContext,
    hrdResponse: HrdResponse,
    correlationId: string
): Promise<CloudEnvironment> {
    const {
        endpoint: authenticationEndpoint,
        tenantId,
        environment,
        configProviderName,
    } = hrdResponse;

    // Extract MSA and Org authentication endpoints from the array -- MSA is optional, Org is required
    const msaEndpoint = authenticationEndpoint.find(ep => ep.type === 'MSA');
    const orgEndpoint = authenticationEndpoint.find(ep => ep.type === 'OrgId' || ep.type === 'Org');
    if (!orgEndpoint) {
        throw new Error('Org auth endpoint not found');
    }

    instrumentation.addStep('FetchOutlookWebOrigin');
    const outlookWebOrigin = await fetchOutlookWebOriginForFederationProvider(
        configProviderName,
        correlationId
    );

    return {
        environmentKey: buildEnvironmentCacheKey(environment, configProviderName),
        msaAuthenticationEndpoint: msaEndpoint?.authentication_endpoint,
        orgAuthenticationEndpoint: orgEndpoint.authentication_endpoint,
        outlookWebOrigin,
        tenantId,
        usesGlobalDirectory: environment === 'Global',
    };
}

function buildEnvironmentCacheKey(
    environment: string,
    configProviderName: string | undefined
): string {
    return new URLSearchParams({
        src: 'OfficeHrd',
        env: environment,
        configProv: configProviderName ?? '',
    }).toString();
}

async function fetchHrdUrl(correlationId: string): Promise<string> {
    const getFederationProviderUrl = OFFICE_CONFIG_SERVICE_URL + FEDERATION_PROVIDER_QUERY_PARAMS;

    return fetchUrlFromOfficeConfigService(getFederationProviderUrl, correlationId);
}

function addDomainQueryParamToHrdUrl(hrdUrl: string, domain: string) {
    return hrdUrl + `?domain=${domain}`;
}

async function fetchOutlookWebOriginForFederationProvider(
    configProviderName: string | undefined,
    correlationId: string
): Promise<string> {
    const officeClientServicesUrl = OFFICE_CONFIG_SERVICE_URL + OUTLOOK_WEB_URL_QUERY_PARAM;
    const getOutlookWebUrl = addFederationProviderQueryParamToUrl(
        officeClientServicesUrl,
        configProviderName
    );

    return fetchUrlFromOfficeConfigService(getOutlookWebUrl, correlationId);
}

function addFederationProviderQueryParamToUrl(url: string, configProviderName: string | undefined) {
    // The order matters, if we do the fp query param first, the config service yields no results
    // If configProviderName is undefined, we don't add the param and it will default to the Global config provider
    return url + (configProviderName ? `&fp=${configProviderName}` : '');
}

async function fetchUrlFromOfficeConfigService(
    requestUrl: string,
    correlationId: string
): Promise<string> {
    const urlResponse = await fetchWithRetry(requestUrl, {
        method: 'GET',
        headers: new Headers({
            ...commonHeaders,
            'X-CorrelationId': correlationId,
            'X-Office-Version': getClientVersion(),
            accept: 'application/json',
        }),
    });

    const response = await urlResponse.json();
    const requestedUrl = response?.['o:OfficeConfig']?.['o:services']?.['o:service']?.['o:url'];

    if (!requestedUrl) {
        const wrappedError: TraceErrorObject = new Error(
            'GetAccountCloudEnvironment_UrlFetch_EmptyUrl'
        );
        wrappedError.additionalInfo = { requestUrl };
        throw wrappedError;
    }

    return requestedUrl;
}

const createRetriableFunc = createRetriableFunction({
    maximumAttempts: 3,
    timeBetweenRetryInMS: createExponentialBackoffFunction(1000),
});

function fetchWithRetry(url: RequestInfo, init: RequestInit): ReturnType<typeof fetchWithTimeout> {
    return createRetriableFunc(() => fetchWithTimeout(url, init)).retriableFunc();
}

function fetchWithTimeout(url: RequestInfo, init: RequestInit): ReturnType<typeof fetch> {
    return new Promise<Response>((resolve, reject) => {
        let controller: AbortController | undefined;
        if ('AbortController' in self) {
            controller = new AbortController();
            init.signal = controller.signal;
        }

        const timeoutMS = 90000;
        const timerHandle: number = self.setTimeout(() => {
            const timeoutError = new Error('Office HRD request timeout');
            if (controller) {
                controller.abort(timeoutError);
            } else {
                reject(timeoutError);
            }
        }, timeoutMS);

        fetch(url, init)
            .then(response => {
                self.clearTimeout(timerHandle);
                resolve(response);
            })
            .catch(err => {
                self.clearTimeout(timerHandle);
                reject(err);
            });
    });
}
